import React, { useMemo } from "react";
import { Box, Center } from "@chakra-ui/react";
import { motion } from "framer-motion";

interface ProgressIndicatorProps {
  size?: number;
  isAnimating?: boolean;
  onClick?: () => void;
}

const baseTransition = {
  ease: "easeInOut",
  repeat: Infinity,
  repeatType: "loop" as const,
};

interface AnimatedDotProps {
  cx: string;
  cy: string;
  r: string;
  delay?: number;
  animate: object;
  duration?: number;
  style?: React.CSSProperties;
}

const AnimatedDot: React.FC<AnimatedDotProps> = React.memo(({ cx, cy, r, delay = 0, animate, duration = 1, style }) => (
  <motion.circle
    cx={cx}
    cy={cy}
    r={r}
    fill="url(#circleGradient)"
    animate={animate}
    style={style}
    transition={{
      ...baseTransition,
      duration,
      delay,
    }}
  />
));

export const ProgressIndicator: React.FC<ProgressIndicatorProps> = React.memo(({ size = 60, isAnimating = true, onClick }) => {
  const colorOne = "#fff3e5";
  const colorTwo = "#e57e00";

  const gradientAnimation = useMemo(
    () =>
      isAnimating
        ? {
            animate: {
              rotate: [0, 360],
            },
            transition: {
              duration: 2,
              ease: "linear",
              repeat: Infinity,
            },
          }
        : {},
    [isAnimating]
  );

  const scaleAnimation = useMemo(
    () => ({
      scale: [0.5, 1.4, 1],
    }),
    []
  );

  const pulseAnimation = useMemo(
    () => ({
      scale: [1, 1.8, 1],
      opacity: [0.7, 1, 0.7],
    }),
    []
  );

  const dotPositions = useMemo(
    () => [
      { cx: "30", cy: "50", delay: 0 },
      { cx: "50", cy: "50", delay: 0.2 },
      { cx: "70", cy: "50", delay: 0.4 },
    ],
    []
  );

  return (
    <Center>
      <Box
        className="ch-project-progress-icon"
        position="relative"
        width={`${size}px`}
        height={`${size}px`}
        onClick={onClick}
        cursor="pointer">
        <svg width={`${size}px`} height={`${size}px`} viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="circleGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={colorOne} />
              <stop offset="100%" stopColor={colorTwo} />
            </linearGradient>
          </defs>
          <motion.circle cx="50" cy="50" r="40" fill="none" stroke="url(#circleGradient)" strokeWidth="15" {...gradientAnimation} />
          {isAnimating ? (
            <>
              {dotPositions.map((pos, index) => (
                <AnimatedDot key={index} cx={pos.cx} cy={pos.cy} r="8" delay={pos.delay} animate={scaleAnimation} />
              ))}
            </>
          ) : (
            <AnimatedDot cx="50" cy="50" r="12" animate={pulseAnimation} duration={1.5} style={{ transformOrigin: "center" }} />
          )}
        </svg>
      </Box>
    </Center>
  );
});

export default ProgressIndicator;
